import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import GuestClaimModal from "@/components/GuestClaimModal";
import {
  X, Users, Clock, FileText, LayoutDashboard, Wallet, Calculator, Briefcase, Settings as SettingsIcon,
} from "lucide-react";

const SEEN_KEY = "ivoliq_area_guide_seen";

// Bereichs-Einführungen. `steps` = einmalige Orientierung (Live + Demo-Bereiche).
// `guestInfo` = Info-Pop-up „das passiert hier im Live-Modus" (Gast, Nicht-Demo-Bereiche).
const AREAS = {
  dashboard: {
    icon: LayoutDashboard,
    steps: [{ title: "Dein Dashboard", text: "Hier siehst du Umsatz, Ausgaben und – besonders wichtig – wie viel du für Steuern zurücklegen solltest. In der Demo mit realistischen Beispielzahlen." }],
  },
  customers: {
    icon: Users,
    steps: [
      { title: "Kunden anlegen", text: "Im Tab Kunden legst du deine Auftraggeber an – mit Adresse fürs Rechnungs-PDF." },
      { title: "Projekte & Aufträge", text: "Im Tab Projekte verwaltest du Aufträge je Kunde und hinterlegst z. B. Stundensätze." },
    ],
  },
  time: {
    icon: Clock,
    steps: [{ title: "Zeiten erfassen", text: "Starte den Timer oder trage Stunden manuell ein. Erfasste Zeiten kannst du später direkt in eine Rechnung übernehmen." }],
  },
  invoices: {
    icon: FileText,
    steps: [{ title: "Rechnungen erstellen", text: "Erstelle in Sekunden eine rechtskonforme Rechnung mit PDF-Vorschau – wahlweise aus erfassten Zeiten." }],
  },
  finances: {
    icon: Wallet,
    guestInfo: { title: "Finanzen (Live-Modus)", text: "Im Live-Modus erfasst du hier Belege – auch per Foto mit automatischer KI-Auslesung von Betrag, Datum und Steuersatz. In der Demo ist dieser Bereich nur zur Erklärung." },
    steps: [{ title: "Finanzen & Belege", text: "Erfasse Ausgaben und Belege – per Foto liest die KI Betrag, Datum und Steuersatz automatisch aus." }],
  },
  taxes: {
    icon: Calculator,
    guestInfo: { title: "Steuern (Live-Modus)", text: "Im Live-Modus berechnet ivoliq hier deine Steuer-Rücklagen, USt-Voranmeldung und Fristen auf Basis deiner echten Daten. In der Demo nur zur Erklärung." },
    steps: [{ title: "Steuern & Rücklagen", text: "Hier siehst du Steuer-Rücklagen, USt-Voranmeldung und wichtige Fristen – automatisch aus deinen Daten berechnet." }],
  },
  advisor: {
    icon: Briefcase,
    guestInfo: { title: "Steuerberater (Live-Modus)", text: "Im Live-Modus teilst du hier ausgewählte Daten sicher mit deiner Kanzlei und tauschst Dokumente aus. In der Demo nur zur Erklärung." },
    steps: [{ title: "Steuerberater-Bereich", text: "Teile ausgewählte Daten sicher mit deiner Kanzlei und tausche Belege & Auswertungen aus." }],
  },
  settings: {
    icon: SettingsIcon,
    guestInfo: { title: "Einstellungen (Live-Modus)", text: "Im Live-Modus hinterlegst du hier Firmendaten, Logo, Steuernummer und Rechnungsdesign. In der Demo nur zur Erklärung." },
    steps: [{ title: "Einstellungen", text: "Hinterlege Firmendaten, Logo, Steuernummer und dein Rechnungsdesign – die Basis für korrekte Rechnungen." }],
  },
};

const GUEST_INFO_ONLY = new Set(["advisor", "settings"]);

function areaForPath(pathname) {
  if (pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname === "/customers" || pathname === "/projects") return "customers";
  if (pathname === "/time-tracking") return "time";
  if (pathname === "/invoices") return "invoices";
  if (pathname === "/finances" || pathname.startsWith("/expense/")) return "finances";
  if (pathname === "/taxes") return "taxes";
  if (pathname === "/steuerberater-bereich") return "advisor";
  if (pathname === "/settings") return "settings";
  return null;
}

export default function AreaGuide() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logGuestEvent } = useAuth();
  const isGuest = !!user?.is_guest;
  const [area, setArea] = useState(null);
  const [step, setStep] = useState(0);
  const [claimOpen, setClaimOpen] = useState(false);

  // „Schon gesehen"-Status im Speicher; Quelle je nach Nutzertyp:
  // Gäste → sessionStorage (pro Sitzung), Live-Nutzer → serverseitig am Konto
  // (geräte-/browserübergreifend, erscheint pro Bereich nur einmal).
  const seenRef = useRef({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user) { seenRef.current = {}; setLoaded(false); return; }
    if (isGuest) {
      try { seenRef.current = JSON.parse(window.sessionStorage.getItem(SEEN_KEY) || "{}"); }
      catch { seenRef.current = {}; }
      setLoaded(true);
      return;
    }
    setLoaded(false);
    api.get("/settings")
      .then((res) => { if (active) seenRef.current = res.data?.area_guides_seen || {}; })
      .catch(() => { if (active) seenRef.current = {}; })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [user, isGuest]);

  const markSeen = useCallback((key) => {
    seenRef.current = { ...seenRef.current, [key]: true };
    if (isGuest) {
      try { window.sessionStorage.setItem(SEEN_KEY, JSON.stringify(seenRef.current)); } catch { /* ignore */ }
    } else {
      api.put("/settings", { area_guides_seen: seenRef.current }).catch(() => { /* ignore */ });
    }
  }, [isGuest]);

  useEffect(() => {
    if (!user || !loaded) { setArea(null); return; }
    const key = areaForPath(location.pathname);
    // Kein Reset, wenn wir uns innerhalb desselben Bereichs bewegen (z. B. Tab-Sprung Kunden→Projekte)
    if (area && key === area) return;
    if (!key || !AREAS[key]) { setArea(null); return; }
    // Gäste: Finanzen/Steuern/Steuerberater/Einstellungen zeigen eine eigene Sperrseite
    // (GuestAreaLock) mit dem Live-Modus-Hinweis — kein zusätzliches Popup nötig.
    if (isGuest && GUEST_INFO_ONLY.has(key)) { setArea(null); return; }
    if (seenRef.current[key]) { setArea(null); return; }
    setStep(0);
    setArea(key);
  }, [location.pathname, user, loaded, area, isGuest]);

  if (!area) return null;

  const cfg = AREAS[area];
  const useGuestInfo = isGuest && GUEST_INFO_ONLY.has(area) && cfg.guestInfo;
  const steps = useGuestInfo ? [cfg.guestInfo] : cfg.steps;
  const Icon = cfg.icon;
  const s = steps[Math.min(step, steps.length - 1)];
  const isLast = step >= steps.length - 1;

  const close = () => {
    markSeen(area);
    // Gast-Trichter: erste gesehene Einführung zählt als „Tour abgeschlossen"
    if (isGuest && logGuestEvent) {
      try {
        if (!window.sessionStorage.getItem("ivoliq_guest_tour_logged")) {
          window.sessionStorage.setItem("ivoliq_guest_tour_logged", "1");
          logGuestEvent("tour_completed");
        }
      } catch { /* ignore */ }
    }
    setArea(null);
  };

  const goNext = () => {
    const nextStep = step + 1;
    // Tab-Sprung: bei „Kunden & Aufträge" auf den Projekte-Tab (/projects) wechseln
    if (area === "customers" && nextStep === 1) {
      navigate("/projects");
    }
    setStep(nextStep);
  };

  return (
    <>
      <div className="fixed inset-0 z-[55] bg-slate-900/40 backdrop-blur-[2px]" onClick={close} data-testid="area-guide-overlay" />
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        data-testid="area-guide"
      >
        <button onClick={close} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600" aria-label="Schließen" data-testid="area-guide-close">
          <X className="h-5 w-5" />
        </button>
        <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
          <Icon className="h-6 w-6 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-emerald-600" : "w-1.5 bg-slate-200"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {/* CTA für Gast-Info-Bereiche: direkt Konto sichern */}
            {useGuestInfo && (
              <Button
                size="sm" variant="outline"
                onClick={() => setClaimOpen(true)}
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="area-guide-claim-btn"
              >
                Konto sichern
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => (isLast ? close() : goNext())}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="area-guide-next"
            >
              {isLast ? "Verstanden" : "Weiter"}
            </Button>
          </div>
        </div>
      </div>
      {isGuest && <GuestClaimModal open={claimOpen} onOpenChange={setClaimOpen} />}
    </>
  );
}

// Reset für „Produkttour erneut starten" (Einstellungen)
export function resetAreaGuides() {
  try { window.localStorage.removeItem(SEEN_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(SEEN_KEY); } catch { /* ignore */ }
  // Serverseitigen Status (Live-Nutzer) ebenfalls zurücksetzen
  api.put("/settings", { area_guides_seen: {} }).catch(() => { /* ignore */ });
}
